import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowRight } from "lucide-react";
import { fetchJob } from "@/lib/api";
import { Markdown } from "@/components/markdown";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { buttonClasses } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Container } from "@/components/container";

export const Route = createFileRoute("/jobs/$slug/")({ component: JobDetail });

function JobDetail() {
  const { slug } = useParams({ from: "/jobs/$slug/" });
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["job", slug], queryFn: () => fetchJob(slug) });

  if (isLoading) {
    return (
      <Container width="lg" className="py-16">
        <div className="space-y-6"><Skeleton className="h-4 w-28" /><Skeleton className="h-12 w-3/4" /><Skeleton className="h-64 w-full" /></div>
      </Container>
    );
  }

  if (isError || !data) {
    const status = error instanceof Error && "status" in error && typeof error.status === "number" ? error.status : undefined;
    return (
      <Container width="lg" className="py-16">
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>{status === 404 ? "Position closed or not found." : "Could not load this role."}</AlertTitle>
          <AlertDescription>{status === 404 ? "We may have closed this role. Take a look at what's still open." : "Try again in a moment."}</AlertDescription>
        </Alert>
        <Link to="/" className={buttonClasses({ variant: "outline", className: "mt-6" })}>All positions</Link>
      </Container>
    );
  }

  const { job } = data;
  return (
    <Container width="lg" className="py-12">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← All positions</Link>
      <header className="mt-6 mb-8 border-b pb-8">
        <div className="mb-3 flex flex-wrap gap-2">
          {job.metadata.map((item) => <Badge key={item} variant="outline" className="bg-background font-normal">{item}</Badge>)}
          {job.status === "disabled" && <Badge variant="outline" className="bg-muted font-normal">Applications disabled</Badge>}
        </div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{job.title}</h1>
        <p className="mt-3 text-muted-foreground">
          {job.status === "disabled" ? "This role is visible for reference, but applications are currently disabled." : "Read the brief, confirm fit, then submit your application."}
        </p>
      </header>

      <article className="prose max-w-none"><Markdown>{job.description_md}</Markdown></article>

      {job.status === "disabled" && (
        <Alert className="mt-10">
          <AlertCircle aria-hidden="true" />
          <AlertTitle>Applications disabled</AlertTitle>
          <AlertDescription>We are not accepting new applications for this role right now.</AlertDescription>
        </Alert>
      )}

      <div className="mt-10 flex flex-wrap gap-3 border-t pt-8">
        {job.status === "open" && <Link to="/jobs/$slug/apply" params={{ slug }} className={buttonClasses({ size: "lg" })}>Apply for this role <ArrowRight aria-hidden="true" /></Link>}
        <Link to="/" className={buttonClasses({ variant: "outline", size: "lg" })}>See other roles</Link>
      </div>
    </Container>
  );
}
